import { Link } from 'react-router-dom';
import { useSaved } from '../lib/useSaved';
import { CERT_STATE, TYPE_LABEL, formatDate } from '../lib/trust';
import { TypeGlyph } from '../lib/glyphs';
import { CITIES } from '../lib/cities';
import './Saved.css';
import { useT } from '../i18n';

/**
 * Saved places.
 *
 * The whole record is stored rather than an id, so this screen works offline and
 * cannot repeat v2's failure, where Saved Places threw a ReferenceError the
 * moment you saved anything because it looked ids up in a global that no page
 * ever loaded.
 *
 * A certificate can lapse after you save a place, so each entry re-derives its
 * state and says plainly when what you saved is no longer current.
 */
export function Saved() {
    const { t, tn, lang } = useT();
    const { saved, toggle } = useSaved();

    if (saved.length === 0) {
        return (
            <div className="saved">
                <h1 className="saved__title">{t('saved.title')}</h1>
                <div className="saved__empty">
                    <p><strong>{t('saved.emptyTitle')}</strong></p>
                    <p>{t('saved.emptyBody')}</p>
                    <Link className="btn btn--primary" to="/city/hongkong">{t('saved.emptyCta')}</Link>
                </div>
            </div>
        );
    }

    const stale = saved.filter((p) => p.certState === 'expired' || p.certState === 'delisted');

    return (
        <div className="saved">
            <h1 className="saved__title">{t('saved.title')}</h1>

            {stale.length > 0 && (
                <aside className="banner banner--stale">
                    <p>
                        <strong>{tn('saved.staleOne', 'saved.staleOther', stale.length)}</strong>
                        {t('saved.staleBody')}
                    </p>
                </aside>
            )}

            <ul className="saved__list">
                {saved.map((p) => {
                    const state = CERT_STATE[p.certState];
                    const dead = p.certState === 'expired' || p.certState === 'delisted';
                    return (
                        <li key={p.id} className="saved__item">
                            <Link className="saved__link" to={`/city/${p.city}/place/${encodeURIComponent(p.id)}`}>
                                <span className="saved__glyph" aria-hidden="true"><TypeGlyph type={p.type} size={19} /></span>
                                <span className="saved__body">
                                    <span className="saved__name">{p.name}</span>
                                    <span className="saved__meta">
                                        {t(TYPE_LABEL[p.type])} · {CITIES[p.city]?.name ?? p.city}
                                        {p.district ? ` · ${p.district}` : ''}
                                    </span>
                                    {state.label && (
                                        <span className={`saved__state tone-${state.tone}`}>
                                            {t(state.label)}
                                            {dead && p.halal?.validUntil ? ` · ${formatDate(p.halal.validUntil, lang.code)}` : ''}
                                        </span>
                                    )}
                                </span>
                            </Link>
                            <button
                                type="button"
                                className="saved__remove"
                                onClick={() => toggle(p)}
                                aria-label={t('saved.remove', { name: p.name })}
                            >
                                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                                    <path d="M18 6 6 18M6 6l12 12" />
                                </svg>
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
